import React, { useCallback, useEffect, useState } from "react";

import api from "../../util/panelApi";
import { Chip, Modal, PageHead, Table, Tabs, Tiles, fmt, img, when } from "./kit";

const cell = { width: "100%", padding: "5px 8px", border: "1px solid #E5E7EB", borderRadius: 6, fontSize: 13 };
const small = { fontSize: 12, color: "#6B7280" };

const statusChip = (s) =>
  ({
    active: <Chip>Aktif</Chip>,
    pending: <Chip kind="wait">Beklemede</Chip>,
    rejected: <Chip kind="bad">Reddedildi</Chip>,
    banned: <Chip kind="bad">Yasaklı</Chip>,
  }[s] || s);

const payoutChip = (s) =>
  ({
    pending: <Chip kind="wait">Bekliyor</Chip>,
    paid: <Chip>Ödendi</Chip>,
    rejected: <Chip kind="bad">Reddedildi</Chip>,
  }[s] || s);

/// Resim yoksa / acilmazsa adin bas harfi (eski kopya resimler 404 veriyordu)
const Avatar = ({ src, name, size = 40 }) => {
  const [bad, setBad] = useState(false);
  const u = img(src);
  const st = { width: size, height: size, borderRadius: "50%", flex: "none", objectFit: "cover" };
  if (!u || bad)
    return (
      <div style={{ ...st, background: "#EEF2FF", color: "#4F46E5", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: size * 0.4 }}>
        {(name || "?").trim().charAt(0).toUpperCase()}
      </div>
    );
  return <img src={u} alt="" style={st} onError={() => setBad(true)} />;
};

const ara = (q, ...alan) => !q || alan.some((a) => String(a || "").toLowerCase().includes(q.toLowerCase()));

/// BD — ajansların üstündeki kademe. Ajanslar buradan BD'ye bağlanır.
const Bd = () => {
  const [tab, setTab] = useState("bd");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ balance: 0, commission: 0 });
  const [agencies, setAgencies] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ ownerId: "", name: "", commissionPercent: 3, country: "", contact: "" });
  const [q, setQ] = useState("");
  const [found, setFound] = useState(null);
  const [arama, setArama] = useState("");
  const [acik, setAcik] = useState({});
  const [sadeceBagsiz, setSadeceBagsiz] = useState(false);

  /// Kullanıcıyı uygulama içi ID'siyle bulur (Mongo _id de kabul edilir).
  const lookup = async () => {
    setFound(null);
    const r = await api.staffLookup(q.trim());
    if (!r?.status) return window.alert(r?.message || "Kullanıcı bulunamadı");
    setFound(r.account);
    setForm((f) => ({ ...f, ownerId: r.account._id, name: f.name || r.account.name || "" }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.bdList();
    if (r?.status) {
      setRows(r.rows || []);
      setTotals(r.totals || { balance: 0, commission: 0 });
    }
    setLoading(false);
  }, []);

  const loadAgencies = useCallback(async () => {
    setLoading(true);
    const r = await api.agencyListAll();
    if (r?.status) setAgencies(r.agencies || r.rows || []);
    setLoading(false);
  }, []);

  const loadPayouts = useCallback(async () => {
    setLoading(true);
    const r = await api.bdPayouts();
    if (r?.status) setPayouts(r.rows || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (tab === "agencies") loadAgencies();
    if (tab === "payouts") loadPayouts();
  }, [tab, loadAgencies, loadPayouts]);

  const create = async () => {
    if (!form.ownerId || !form.name) return window.alert("Kullanıcı ID ve BD adı gerekli.");
    const r = await api.bdCreate(form);
    if (!r?.status) return window.alert(r?.message || "Oluşturulamadı");
    setOpenNew(false);
    setForm({ ownerId: "", name: "", commissionPercent: 3, country: "", contact: "" });
    load();
  };

  const saveRow = async (b, patch) => {
    const r = await api.bdUpdate({ bdId: b._id, ...patch });
    if (!r?.status) return window.alert(r?.message || "Kaydedilemedi");
    load();
  };

  const remove = async (b) => {
    if (!window.confirm(`${b.name} silinsin mi? Bağlı ${b.agencyCount || 0} ajans BD'siz kalır.`)) return;
    const r = await api.bdDelete({ bdId: b._id });
    if (!r?.status) return window.alert(r?.message || "Silinemedi");
    load();
  };

  const assign = async (agencyId, bdId) => {
    const r = await api.bdAssign({ agencyId, bdId: bdId || null });
    if (!r?.status) return window.alert(r?.message || "Kaydedilemedi");
    loadAgencies();
    load();
  };

  const decide = async (payoutId, state) => {
    const note = state === "rejected" ? window.prompt("Red sebebi (isteğe bağlı)") ?? "" : "";
    const r = await api.bdPayoutUpdate({ payoutId, state, adminNote: note });
    if (!r?.status) return window.alert(r?.message || "Güncellenemedi");
    loadPayouts();
  };

  return (
    <>
      <PageHead
        title="BD"
        sub="Ajansların üstündeki kademe. BD kendi ajanslarını toplar, onların yayıncı cirosundan pay alır."
        right={
          <button className="btn btn-primary btn-sm" onClick={() => setOpenNew(true)}>
            BD oluştur
          </button>
        }
      />

      <Tiles
        items={[
          { label: "BD sayısı", value: fmt(rows.length) },
          { label: "Aktif", value: fmt(rows.filter((r) => r.status === "active").length) },
          { label: "Bağlı ajans", value: fmt(rows.reduce((t, b) => t + (b.agencyCount || 0), 0)) },
          { label: "Toplam pay", value: `${fmt(totals.commission)} coin` },
          { label: "Bekleyen bakiye", value: `${fmt(totals.balance)} coin` },
        ]}
      />

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          ["bd", "BD listesi"],
          ["agencies", "Ajans bağlama"],
          ["payouts", "Ödeme talepleri"],
        ]}
      />

      {(tab === "bd" || tab === "agencies") && (
        <input
          style={{ ...cell, maxWidth: 360, marginBottom: 12, padding: "8px 12px" }}
          placeholder={tab === "bd" ? "BD adı, kod, sahip adı veya ID ara" : "Ajans adı, kod, sahip adı veya ID ara"}
          value={arama}
          onChange={(e) => setArama(e.target.value)}
        />
      )}

      {tab === "bd" && (
        <div style={{ display: "grid", gap: 10 }}>
          {loading && !rows.length && <div style={small}>Yükleniyor…</div>}
          {!loading && !rows.length && <div style={small}>Henüz BD yok.</div>}
          {rows
            .filter((b) => ara(arama, b.name, b.code, b.ownerName, b.ownerUid, ...(b.agencies || []).map((a) => a.name)))
            .map((b) => (
              <div key={b._id} className="card" style={{ margin: 0 }}>
                <div className="card-body" style={{ padding: 14 }}>
                  <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: "wrap" }}>
                    <Avatar src={b.ownerImage} name={b.ownerName} size={48} />
                    <div style={{ minWidth: 160, flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>
                        {b.name} <span style={{ ...small, fontWeight: 500 }}>· {b.code}</span>
                      </div>
                      <div style={small}>
                        {b.ownerName} {b.ownerUid ? `· ID ${b.ownerUid}` : ""} {b.country ? `· ${b.country}` : ""} · {when(b.createdAt)}
                      </div>
                    </div>
                    <div className="d-flex" style={{ gap: 18, flexWrap: "wrap" }}>
                      {[
                        ["Ajans", fmt(b.agencyCount)],
                        ["Yayıncı", fmt(b.hostTotal)],
                        ["Toplam pay", fmt(b.totalCommission)],
                        ["Bakiye", fmt(b.balance)],
                      ].map(([k, v]) => (
                        <div key={k} style={{ textAlign: "center" }}>
                          <div style={{ fontWeight: 700 }}>{v}</div>
                          <div style={small}>{k}</div>
                        </div>
                      ))}
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                      <span style={small}>Pay %</span>
                      <input
                        style={{ ...cell, width: 64 }}
                        type="number"
                        defaultValue={b.commissionPercent}
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (v !== b.commissionPercent) saveRow(b, { commissionPercent: v });
                        }}
                      />
                      {statusChip(b.status)}
                      <select style={{ ...cell, width: 110 }} value={b.status} onChange={(e) => saveRow(b, { status: e.target.value })}>
                        <option value="active">Aktif</option>
                        <option value="pending">Beklemede</option>
                        <option value="banned">Yasaklı</option>
                      </select>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setAcik({ ...acik, [b._id]: !acik[b._id] })}>
                        {acik[b._id] ? "Gizle" : `Ajanslar (${b.agencyCount || 0})`}
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(b)}>
                        Sil
                      </button>
                    </div>
                  </div>
                  {acik[b._id] && (
                    <div style={{ marginTop: 12, borderTop: "1px solid #F1F5F9", paddingTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 8 }}>
                      {!(b.agencies || []).length && <div style={small}>Bu BD'ye bağlı ajans yok. "Ajans bağlama" sekmesinden bağlayabilirsin.</div>}
                      {(b.agencies || []).map((a) => (
                        <div key={a._id} className="d-flex align-items-center" style={{ gap: 8, padding: 8, border: "1px solid #EEF2F7", borderRadius: 10 }}>
                          <Avatar src={a.image} name={a.name} size={34} />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</div>
                            <div style={small}>
                              {a.code} · {fmt(a.hostCount)} yayıncı {a.ownerUid ? `· ID ${a.ownerUid}` : ""}
                            </div>
                          </div>
                          <button className="btn btn-sm btn-link text-danger" title="BD'den ayır" onClick={() => window.confirm(`${a.name} bu BD'den ayrılsın mı?`) && assign(a._id, "")}>
                            Ayır
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}

      {tab === "agencies" && (
        <label className="d-flex align-items-center" style={{ gap: 6, marginBottom: 10, fontSize: 13 }}>
          <input type="checkbox" checked={sadeceBagsiz} onChange={(e) => setSadeceBagsiz(e.target.checked)} />
          Yalnız BD'ye bağlı olmayanlar ({agencies.filter((a) => !a.bdId).length})
        </label>
      )}
      {tab === "agencies" && (
        <Table
          head={["Ajans", "Sahip", "Yayıncı", "Ajans payı", "Bağlı BD"]}
          loading={loading}
          empty="Ajans yok."
          rows={agencies
            .filter((a) => (!sadeceBagsiz || !a.bdId) && ara(arama, a.name, a.code, a.ownerName, a.ownerUid))
            .map((a) => (
            <tr key={a._id}>
              <td>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <Avatar src={a.logo} name={a.name} size={36} />
                  <div>
                    <b>{a.name}</b>
                    <div style={small}>{a.code}</div>
                  </div>
                </div>
              </td>
              <td>
                {a.ownerName}
                {a.ownerUid && <div style={small}>ID {a.ownerUid}</div>}
              </td>
              <td>{fmt(a.hostCount)}</td>
              <td>%{a.commissionPercent}</td>
              <td style={{ minWidth: 190 }}>
                <select style={cell} value={a.bdId || ""} onChange={(e) => assign(a._id, e.target.value)}>
                  <option value="">— bağlı değil —</option>
                  {rows
                    .filter((b) => b.status === "active")
                    .map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.name} (%{b.commissionPercent})
                      </option>
                    ))}
                </select>
              </td>
            </tr>
          ))}
        />
      )}

      {tab === "payouts" && (
        <Table
          head={["BD", "Tutar", "Yöntem", "Hesap", "Tarih", "Durum", ""]}
          loading={loading}
          empty="Ödeme talebi yok."
          rows={payouts.map((p) => (
            <tr key={p._id}>
              <td>
                <b>{p.bdName}</b>
              </td>
              <td>{fmt(p.coin)} coin</td>
              <td>{p.method || "—"}</td>
              <td>{p.account || "—"}</td>
              <td>{when(p.createdAt)}</td>
              <td>{payoutChip(p.state)}</td>
              <td>
                {p.state === "pending" && (
                  <div className="d-flex" style={{ gap: 6 }}>
                    <button className="btn btn-sm btn-primary" onClick={() => decide(p._id, "paid")}>
                      Ödendi
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => decide(p._id, "rejected")}>
                      Reddet
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        />
      )}

      <Modal open={openNew} title="BD oluştur" onClose={() => setOpenNew(false)}>
        <div style={{ ...small, marginBottom: 12 }}>
          BD sahibi bir kullanıcı hesabıdır; oluşturduktan sonra uygulamada Profil → BD Merkezi sayfasını görür.
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <div className="d-flex" style={{ gap: 6 }}>
            <input
              style={cell}
              placeholder="Kullanıcı ID (uygulamadaki ID)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
            />
            <button className="btn btn-sm btn-outline-secondary" onClick={lookup}>
              Bul
            </button>
          </div>
          {found && (
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Avatar src={found.image} name={found.name} size={32} /><b>{found.name}</b>
              <span style={small}>ID {found.uniqueID}</span>
            </div>
          )}
          <input
            style={cell}
            placeholder="BD adı"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input
            style={cell}
            type="number"
            placeholder="Pay yüzdesi"
            value={form.commissionPercent}
            onChange={(e) => setForm({ ...form, commissionPercent: Number(e.target.value) })}
          />
          <input
            style={cell}
            placeholder="Ülke / bölge"
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
          />
          <input
            style={cell}
            placeholder="İletişim"
            value={form.contact}
            onChange={(e) => setForm({ ...form, contact: e.target.value })}
          />
          <button className="btn btn-primary btn-sm" onClick={create}>
            Oluştur
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Bd;
